import { FunctionComponent, ReactElement, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useCustomerSiteOptions, useCustomerUsers } from '@/queries/customers';
import { useQueryClient } from '@tanstack/react-query';
import { useRoles, useRolesHierarchy } from '@/queries/users';
import { CustomerUser, Site } from '@/core/entities';
import UserSitesListDialog from '@/features/users/UserSitesListDialog';
import { Roles } from '@/core/types';
import { CustomerContext } from '../index/Customer';
import { Button, IconButton, Stack } from '@mui/material';
import { AddRounded, EditRounded, DeleteRounded } from '@mui/icons-material';
import DataGrid, { Column, FilterRow, HeaderFilter, Paging, SearchPanel, Sorting, Toolbar, Item } from 'devextreme-react/data-grid';
import Backdrop from '@/components/ui/backdrop';
import UserForm from '@/features/users/forms/user-form/UserForm';
import DeleteUserForm from '@/features/users/forms/DeleteUserForm';
import { UserFormModel } from '@/features/users/forms/user-form/types';
import {
	initialCustomerUserFormModel,
	userFormModelFromCustomerUser,
} from '@/features/users/forms/user-form/user-form.defaults';
import SitesCell from '@/features/users/customer-users-table/cells/SitesCell';

const CustomerUsersPage: FunctionComponent = (): ReactElement => {
	const queryClient = useQueryClient();
	const { customerId } = useParams();

	const { customer } = useOutletContext<CustomerContext>();

	const [users, setUsers] = useState<CustomerUser[]>([]);

	// User Sites dialog
	const [userSitesDialog, setUserSitesDialog] = useState<{
		open: boolean;
		user?: string;
		sites?: Site[];
		customerId?: string;
	}>({
		open: false,
	});

	const { data: siteOptions } = useCustomerSiteOptions(customerId);
	const { data: roles } = useRoles();
	const { data: rolesHierarchy } = useRolesHierarchy();

	const roleOptions = useMemo(
		() => roles?.filter((role) => role.title !== Roles.LEVIANT_ADMIN),
		[roles]
	);

	const { queryKey: useUsersQueryKey, query: useUsersQuery } = useCustomerUsers({
		customerId,
		queryParams: {
			limit: 99999,
		},
	});

	const { data: userPages, isLoading } = useUsersQuery;

	useEffect(() => {
		if (userPages) {
			setUsers(userPages.pages[0].data);
		}
	}, [userPages]);

	const refetchCustomerUsers = () => {
		/* eslint-disable @typescript-eslint/no-floating-promises */
		queryClient.invalidateQueries(useUsersQueryKey);
	};

	// User form
	const [userForm, setUserForm] = useState<{
		open: boolean;
		formModel: UserFormModel;
		user?: CustomerUser;
	}>({
		open: false,
		formModel: initialCustomerUserFormModel,
	});

	// Delete form
	const [deleteUserForm, setDeleteUserForm] = useState<{ open: boolean; model?: CustomerUser }>({
		open: false,
		model: undefined,
	});

	const isUserFormDisabled: boolean = useMemo(() => {
		const { firstName, lastName, email, roleId, sites } = userForm.formModel;
		const isDisabled = !firstName.length || !lastName.length || !roleId.length || !sites?.length;

		const operatorRoleId = roles?.find((role) => role.title === Roles.OPERATOR)?.id;
		if (roleId === operatorRoleId) {
			return isDisabled;
		}

		return isDisabled || !email || !email.length;
	}, [userForm.formModel, roles]);

	const hasPermissionForAction = (customerUser: CustomerUser) =>
		!!rolesHierarchy?.map((role) => role.id).includes(customerUser.roleId);

	const handleEdit = (customerUser: CustomerUser) => {
		if (!hasPermissionForAction(customerUser)) return;
		setUserForm({
			formModel: userFormModelFromCustomerUser(customerUser),
			user: customerUser,
			open: true,
		});
	};

	const handleDelete = (customerUser: CustomerUser) => {
		if (!hasPermissionForAction(customerUser)) return;
		setDeleteUserForm({ model: customerUser, open: true });
	};

	const handleShowMoreSites = (customerUser: CustomerUser) => {
		setUserSitesDialog({
			open: true,
			sites: customerUser.sites,
			user: `${customerUser.firstName || ''} ${customerUser.lastName || ''}`,
			customerId: customerUser.customer?.id,
		});
	};

	const getRoleTitle = (roleId: string): string => {
		const role = roleOptions?.find((r) => r.id === roleId);
		return role?.title || '-';
	};

	const roleFilterOptions = useMemo(
		() => roleOptions?.map((role) => ({ text: role.title, value: role.title })) || [],
		[roleOptions]
	);

	const renderSitesCell = (cellData: { data: CustomerUser }) => {
		const customerUser = cellData.data;
		return (
			<SitesCell
				sites={customerUser.sites}
				onClick={() => handleShowMoreSites(customerUser)}
			/>
		);
	};

	const renderActionsCell = (cellData: { data: CustomerUser }) => {
		const customerUser = cellData.data;
		const hasPermission = hasPermissionForAction(customerUser);

		return (
			<Stack direction="row" spacing={1}>
				<IconButton
					size="small"
					color="primary"
					disabled={!hasPermission}
					onClick={(e) => {
						e.stopPropagation();
						handleEdit(customerUser);
					}}
				>
					<EditRounded fontSize="small" />
				</IconButton>
				<IconButton
					size="small"
					color="error"
					disabled={!hasPermission}
					onClick={(e) => {
						e.stopPropagation();
						handleDelete(customerUser);
					}}
				>
					<DeleteRounded fontSize="small" />
				</IconButton>
			</Stack>
		);
	};

	if (!roleOptions) return <Backdrop />;

	return (
		<>
			<Stack direction="column" spacing={2.5}>
				{isLoading ? (
					<Backdrop />
				) : (
					<DataGrid
						dataSource={users}
						showBorders={true}
						columnAutoWidth={true}
						rowAlternationEnabled={true}
						allowColumnResizing={true}
						allowColumnReordering={true}
					>
						<Sorting mode="multiple" />
						<FilterRow visible={true} />
						<HeaderFilter visible={true} />
						<SearchPanel visible={true} highlightCaseSensitive={true} />
						<Paging defaultPageSize={50} enabled={true} />
						<Toolbar>
							<Item location="before">
								<Button
									variant="contained"
									startIcon={<AddRounded />}
									size="large"
									onClick={() =>
										setUserForm({
											user: undefined,
											open: true,
											formModel: initialCustomerUserFormModel,
										})
									}
								>
									Create User
								</Button>
							</Item>
							<Item name="searchPanel" location="after" />
						</Toolbar>

						<Column dataField="firstName" caption="First Name" />
						<Column dataField="lastName" caption="Last Name" />
						<Column dataField="email" caption="Email" />
						<Column
							caption="Site"
							cellRender={renderSitesCell}
							calculateCellValue={(rowData: CustomerUser) =>
								rowData.sites?.map((site) => site.name).join(', ') || '-'
							}
						/>
						<Column
							caption="Role"
							dataField="roleId"
							calculateCellValue={(rowData: CustomerUser) => getRoleTitle(rowData.roleId)}
							allowSorting={true}
							allowFiltering={true}
						>
							<HeaderFilter dataSource={roleFilterOptions} />
						</Column>
						<Column dataField="pin" caption="PIN" alignment="right" />
						<Column
							caption="Actions"
							width={120}
							allowFiltering={false}
							allowSorting={false}
							allowReordering={false}
							cellRender={renderActionsCell}
						/>
					</DataGrid>
				)}
			</Stack>
			<UserForm
				open={userForm.open}
				onClose={() => setUserForm({ ...userForm, open: false })}
				formModel={userForm.formModel}
				updateFormModel={(value: UserFormModel) =>
					setUserForm({ ...userForm, formModel: { ...userForm.formModel, ...value } })
				}
				disabled={isUserFormDisabled}
				user={userForm.user}
				customer={customer}
				onSubmitted={refetchCustomerUsers}
				sites={siteOptions}
			/>
			<DeleteUserForm
				open={deleteUserForm.open}
				onClose={() => setDeleteUserForm({ ...deleteUserForm, open: false })}
				user={deleteUserForm.model}
				onSubmitted={refetchCustomerUsers}
				customerName={customer?.name}
			/>
			<UserSitesListDialog
				open={userSitesDialog.open}
				onClose={() => setUserSitesDialog({ ...userSitesDialog, open: false })}
				user={userSitesDialog.user}
				sites={userSitesDialog.sites}
				customerId={userSitesDialog.customerId}
			/>
		</>
	);
};

export default CustomerUsersPage;
